import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { AppDispatch, AppGetState } from '@/store'
import Api from '@/services/http'
import { commonUrl, userUrl } from '@/services/url'
import type * as Types from './types'
import { getSellerBasicInfo, getSalesPlatform } from '@/services/api'
import { SELLEROWNERTYPE } from '@/constants/common'
import { jsonParse } from '@/utils'
import type { UploadRestrictItem } from '@/services/api/resource/type'
import type { UploadScene } from '@/constants/resource'

// 优先排序
// USD放在第一个，TWD放在第二个，其他顺序不变
/**
 * TODO: 会存在bug，空数据的情况下出去是个异常 list: [...priorityList, ...lowPriorityList]
 * @param currency 币种信息枚举
 * @returns
 */
const sortCurrency = (currency: Types.IEnumsItem) => {
  const list = currency?.list || []
  const priorityCode = ['USD', 'TWD']
  const priorityList = priorityCode.reduce((pre, cur) => {
    const item = list.find(({ code }) => code === cur)
    return [...pre, item]
  }, [])
  const lowPriorityList = list.filter(({ code }) => priorityCode.indexOf(code as string) === -1)
  currency.list = [...priorityList, ...lowPriorityList]
  return currency
}
const initialState: Types.IInitialState = {
  enums: null,
  warehouseInfoList: [],
  autoWarehouseList: [],
  storeList: null,
  hideSelectAllTips: !!localStorage.getItem('hideSelectAllTips') || false,
  offsetHeader: 0,
  isMultipleWarehouse: false,
  isMultipleWarehouseEnable: false,
  isBandSalesPlatform: false,
  i18nLogisticsInfo: null,
  isUniqueSeller: null,
  sellerBasicInfo: {},
  allHeaderColumns: [],
  importConfig: null,
}

const commonSlice = createSlice({
  name: 'common',
  initialState: initialState,
  reducers: {
    setEnums(state, action: PayloadAction<Types.IEnums>) {
      return {
        ...state,
        enums: action.payload,
      }
    },

    setWarehouseInfoList(state, action: PayloadAction<Types.IWarehouseInoList[]>) {
      // 转为json
      const warehouseInfoList = [...action.payload].map((item) => {
        const providerFieldConfigs = item.providerFieldConfigs
          ? item.providerFieldConfigs.map((config) => ({
              ...config,
              fieldConfig: jsonParse<Types.GoodFieldConfigType, any>(config.fieldConfig),
            }))
          : []

        return {
          ...item,
          providerFieldConfigs,
        }
      })

      return {
        ...state,
        warehouseInfoList,
      }
    },

    setAutoWarehouseList(state, action: PayloadAction<string[]>) {
      return {
        ...state,
        autoWarehouseList: action.payload,
      }
    },

    setStoreList(state, action: PayloadAction<Types.IStoreListItem[]>) {
      return {
        ...state,
        storeList: action.payload,
      }
    },

    setHideSelectAllTips(state, action: PayloadAction<boolean>) {
      localStorage.setItem('hideSelectAllTips', `${action.payload}`)
      return {
        ...state,
        hideSelectAllTips: action.payload,
      }
    },
    setBandSalesPlatform(state, action: PayloadAction<boolean>) {
      localStorage.setItem('isBandSalesPlatform', `${action.payload}`)
      return {
        ...state,
        isBandSalesPlatform: action.payload,
      }
    },
    /** 设置是否开启了多仓（不校验多仓启用状态） */
    setIsMultipleWarehouse(state, action: PayloadAction<any>) {
      return {
        ...state,
        isMultipleWarehouse: action.payload,
      }
    },
    /** 设置是否开启了多仓（多个仓库启用） */
    setIsMultipleWarehouseEnable(state, action: PayloadAction<any>) {
      return {
        ...state,
        isMultipleWarehouseEnable: action.payload,
      }
    },
    /** 设置多语言仓库信息 */
    setI18nLogisticsInfo(state, action: PayloadAction<Types.II18nWarehouse[]>) {
      return {
        ...state,
        i18nLogisticsInfo: action.payload,
      }
    },
    /** 设置是否统一货主 */
    setIsUniqueSeller(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isUniqueSeller: action.payload,
      }
    },
    /** 设置商家基础信息 */
    setSellerBasicInfo(state, action: PayloadAction<Types.IInitialState['sellerBasicInfo']>) {
      return {
        ...state,
        sellerBasicInfo: action.payload,
      }
    },
    /** 设置表头数据 */
    setAllHeaderColumns(state, action: PayloadAction<Types.IHeaderColumns[]>) {
      return {
        ...state,
        allHeaderColumns: action.payload,
      }
    },
    /** 导入配置 */
    setImportConfig(state, action: PayloadAction<UploadRestrictItem[]>) {
      const mapData = action.payload?.reduce((result, item) => {
        return Object.assign(result, {
          [item.sceneId]: item,
        })
      }, {} as Record<UploadScene, UploadRestrictItem>)
      return {
        ...state,
        importConfig: mapData,
      }
    },
  },
})

export const {
  setEnums,
  setBandSalesPlatform,
  setWarehouseInfoList,
  setAutoWarehouseList,
  setStoreList,
  setHideSelectAllTips,
  setIsMultipleWarehouse,
  setIsMultipleWarehouseEnable,
  setI18nLogisticsInfo,
  setIsUniqueSeller,
  setSellerBasicInfo,
  setAllHeaderColumns,
  setImportConfig,
} = commonSlice.actions

/**
 * 获取所有枚举值以及销售平台列表
 * @returns
 */
export const getAllEnums = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { common } = getState()

  if (common.enums) {
    return common.enums
  }
  /** TODO: getSalesPlatform 可以不需要传{}, 方法定义改成?类型 */
  const [resp, platformRes] = await Promise.all([Api.admin.get<Types.IEnumsMap>(commonUrl.enums), getSalesPlatform({})])
  // TODO:  axios判断了success了，这里仍然需要判断吗？
  if (resp.code === 'SUCCESS') {
    const result = resp.data.enumMap
    const currency = sortCurrency(result.currency)
    // TODO: 尽量不要改源数据
    result.currency = currency
    if (platformRes.code === 'SUCCESS') {
      // 产品要求动态可配，因为需要做一个字典组合与nacos组合的拼接
      result.salesPlatform = { ...result.salesPlatform, list: platformRes.data?.map((i) => ({ ...i, desc: i.label, code: i.code })) }
    }
    dispatch(setEnums(result))
    return result
  }

  return null
}

/** 获取商家仓库信息 */
export const getWarehoseInfo = () => async (dispatch: AppDispatch) => {
  const resp = await Api.admin.post<Types.IWarehouseInoList[]>('/api/seller-express/list')

  if (resp.code === 'SUCCESS') {
    dispatch(setWarehouseInfoList(resp.data))
    dispatch(setIsMultipleWarehouse(resp.data.length > 1))
    dispatch(setIsMultipleWarehouseEnable(resp.data?.filter((item) => item.enable)?.length > 1))
  }

  return resp
}

/**
 * 获取商家店铺的信息
 */
export const getStoreInfo = () => async (dispatch: AppDispatch) => {
  const resp = await Api.admin.get(userUrl.bindStoreList)

  if (resp.code === 'SUCCESS') {
    dispatch(setStoreList(resp.data.list))
  }

  return resp
}

/** 获取是否绑定了可以上传同步的销售平台 */
export const getBandSalesPlatform = () => async (dispatch: AppDispatch) => {
  const resp = await Api.admin.get(commonUrl.BandSalesPlatform)
  if (resp.code === 'SUCCESS') {
    dispatch(setBandSalesPlatform(resp?.data?.skip || false))
  }

  return resp
}

/**
 * 获取仓储商家基本信息
 * TODO: tagee 没写需要传，data可以不传
 * TODO: 与后端确认可以与user/get接口合并为同一个
 *  */
export const getSellerOwnerType = () => async (dispatch: AppDispatch) => {
  const { data } = await getSellerBasicInfo()
  dispatch(setIsUniqueSeller(data.sellerOwnerType === SELLEROWNERTYPE.UNIQUE))

  const isPrepaid = data.deliverySettlementMethod === 'prepaid'
  dispatch(
    setSellerBasicInfo({
      ...data,
      isPrepaid,
    })
  )
}

export default commonSlice.reducer
